import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigationType, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { store, resetState } from "@services/redux/store";
import { nextStep, prevStep } from "@services/redux/slices/stepSlice";
import { updateField } from "./helpers/helpers";
import TopBanner from "./components/TopBanner";
import ScrollIndicator from "@components/ScrollIndicator";
import Product from "./Product";
import { STEP_LEVEL_MAP } from "./helpers/mappings";

const Operations = ({ profile }) => {
  const { t } = useTranslation();
  const [product, setProduct] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPath = useRef(location.pathname);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const step = useSelector((s) => s.step);
  const transport = useSelector((s) => s.transport);

  const hasImages =
    Array.isArray(transport?.step2?.images) &&
    transport.step2.images.some((arr) => Array.isArray(arr) && arr.length > 0);

  const scrollTop = () => window.scrollTo(0, 0);

  const handleNextStep = () => {
    setIsLoading(true);
    setTimeout(() => {
      dispatch(nextStep());
      scrollTop();
      setIsLoading(false);
    }, 1);
  };

  const handlePrevStep = () => {
    dispatch(prevStep());
    scrollTop();
  };

  const resetReduxState = () => {
    store.dispatch(resetState());
  };

  // Restore product from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("product");
    if (saved) setProduct(saved);
  }, []);

  // Handle refresh with uploaded images
  useEffect(() => {
    const beforeUnload = (e) => {
      if (!hasImages) return;
      sessionStorage.setItem("refreshConfirmed", "pending");
      e.preventDefault();
      e.returnValue =
        t("warning_images_lost") ||
        "You have uploaded images that will be lost if you refresh. You'll need to re-upload them.";
    };

    const onUnload = () => {
      if (hasImages && sessionStorage.getItem("refreshConfirmed") === "pending")
        sessionStorage.setItem("refreshConfirmed", "true");
    };

    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("unload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("unload", onUnload);
    };
  }, [hasImages, t]);

  // Clear images if refresh confirmed
  useEffect(() => {
    const flag = sessionStorage.getItem("refreshConfirmed");
    if (flag === "true") {
      updateField(dispatch, "images", []);
      sessionStorage.removeItem("refreshConfirmed");
    } else if (flag === "pending") {
      sessionStorage.removeItem("refreshConfirmed");
    }
  }, [dispatch]);

  // Handle leaving hosting section
  useEffect(() => {
    const leavingHosting =
      prevPath.current.startsWith("/hosting") &&
      !location.pathname.startsWith("/hosting") &&
      navigationType !== "POP";

    if (leavingHosting && hasImages) {
      const confirmLeave = window.confirm(
        t("warning_images_lost") ||
          "You have uploaded images that will be lost if you leave. You'll need to re-upload them."
      );

      if (!confirmLeave) {
        navigate(prevPath.current, { replace: true });
        return;
      }
      resetReduxState();
    }

    prevPath.current = location.pathname;
  }, [location.pathname, navigationType, hasImages, navigate, t]);

  // 🔹 Derive separators and totalSteps from STEP_LEVEL_MAP
  const levels = STEP_LEVEL_MAP[product] || {};
  const stepKeys = Object.keys(levels)
    .map(Number)
    .sort((a, b) => a - b);
  const separatorOne = stepKeys[0] || -1;
  const separatorTwo = stepKeys[1] || -1;
  const totalSteps = stepKeys.at(-1) || 1;

  // Redirect to product selection if missing
  useEffect(() => {
    const saved = localStorage.getItem("product");
    if (saved) {
      setProduct(saved);
    } else {
      navigate("/new-product", { replace: true });
    }
  }, [navigate]);

  // 🔸 Props for Product
  const stepProps = {
    step,
    t,
    isLoading,
    handleNextStep,
    handlePrevStep,
    profile,
    separatorOne,
    separatorTwo,
    totalSteps,
  };
  // console.log(stepProps);
  return (
    <section className="w-100">
      {/* {step} */}
      <TopBanner step={step} />
      <Product productType={product} {...stepProps} />
      <ScrollIndicator />
    </section>
  );
};

export default Operations;
